import './dropdown-with-timeframe.component.scss';
import 'react-datepicker/dist/react-datepicker.css';

import { useEffect, useState, useCallback, memo } from 'react';
import { cloneDeep } from 'lodash';
import { useTranslation } from "react-i18next";
import DatePicker from 'react-datepicker';
import dayjs from 'dayjs';

import ButtonWithIcon from '../ButtonWithIcon';
import RadioButton from '../RadioButton';

import { timeFormat } from '../../const/nuclias/dateFormat';

const defaultTimeframe = {
  last24hrs: {
    id: 'last24hrs',
    label: '6339866168', // Last 24 hours
    timeframe: 86400,
    dayCount: 1,
    postTimeStamp: {
      fromDate: null,
      toDate: null
    },
    checked: true,
    isHide: false
  },
  last7days: {
    id: 'last7days',
    label: '4b299435d7', // Last 7 days
    timeframe: 7 * 86400,
    dayCount: 7,
    postTimeStamp: {
      fromDate: null,
      toDate: null
    },
    checked: false,
    isHide: false
  },
  last14days: {
    id: 'last14days',
    label: 'beab46acaf', // Last 14 days
    timeframe: 14 * 86400,
    dayCount: 14,
    postTimeStamp: {
      fromDate: null,
      toDate: null
    },
    checked: false,
    isHide: false
  },
  last30days: {
    id: 'last30days',
    label: '2557491405', // Last 30 days
    timeframe: 30 * 86400,
    dayCount: 30,
    postTimeStamp: {
      fromDate: null,
      toDate: null
    },
    checked: false,
    isHide: false
  },
  last60days: {
    id: 'last60days',
    label: 'a0364e63d8', // Last 60 days
    timeframe: 60 * 86400,
    dayCount: 60,
    postTimeStamp: {
      fromDate: null,
      toDate: null
    },
    checked: false,
    isHide: false
  },
  customRange: {
    id: 'customRange',
    label: '6b49896c29', // Custom range (Last xxx days)';
    timeframe: null,
    dayCount: null,
    isOpen: {
      fromDate: false,
      toDate: false
    },
    rawDate: {
      fromDate: null,
      toDate: null
    },
    displayDate: {
      fromDate: null,
      toDate: null
    },
    postTimeStamp: {
      fromDate: null,
      toDate: null
    },
    checked: false,
    datePicked: false,
    isHide: false
  }
}

const DropdownWithTimeframe = props => {
  const {
    customDefaultTimeframe, // Please follow the defaultTimeframe data structure format.
    customTimeFrameDay, // '365' or '60'...
    customHideItem, // ['last24hrs', 'last60days', 'customRange', ...],
    forceRerenderObject, // For forcing rerender once this object is changed.
    onInit,
    onChange,
    alignEnd,
    disabled
  } = props;

  const { t } = useTranslation();
  const alignEndClassName = !!alignEnd ? 'dropdown-menu-end' : '';
  const [newTimeframe, setNewTimeframe] = useState(cloneDeep(customDefaultTimeframe));
  const [selectedTimeframe, setSelectedTimeframe] = useState(null);

  const convertDateToDayjs = useCallback(date => dayjs(date), []);
  const convertDayjsToDate = useCallback(date => dayjs(date).toDate(), []);
  const convertDateFormat = useCallback(date => dayjs(date).format(timeFormat.yDate), []);
  const convertDateToTimeStamp = useCallback(date => dayjs(date).unix(), []);
  const convertDateToStartOfTimeStamp = useCallback(date => dayjs(date).startOf('day').unix(), []);

  const updateSelectedTimeframe = (source, timeframeKey = null) => {
    let updatedTimeframe = {};
    let selectedTimeframeKey = timeframeKey || null;

    Object.keys(source).forEach(key => {
      const date = dayjs();

      if (!selectedTimeframeKey && source[key].checked) {
        selectedTimeframeKey = key;
      }

      updatedTimeframe[key] = { ...source[key] };
      updatedTimeframe[key].checked = (key === selectedTimeframeKey);

      if (key === selectedTimeframeKey && key !== 'customRange') {
        updatedTimeframe[key].postTimeStamp['fromDate'] = convertDateToTimeStamp(date.subtract(updatedTimeframe[key].dayCount, 'day'));
        updatedTimeframe[key].postTimeStamp['toDate'] = convertDateToTimeStamp(date);
      }

      if (key === selectedTimeframeKey && key === 'customRange') {
        updatedTimeframe[key].label = t(updatedTimeframe[key].label, { days: customTimeFrameDay || '365' });
        updatedTimeframe[key].rawDate['fromDate'] = date.subtract(customTimeFrameDay, 'day');
        updatedTimeframe[key].rawDate['toDate'] = date;
        updatedTimeframe[key].displayDate['fromDate'] = convertDateFormat(updatedTimeframe[key].rawDate['fromDate']);
        updatedTimeframe[key].displayDate['toDate'] = convertDateFormat(updatedTimeframe[key].rawDate['toDate']);
        updatedTimeframe[key].postTimeStamp['fromDate'] = convertDateToStartOfTimeStamp(updatedTimeframe[key].rawDate['fromDate']);
        updatedTimeframe[key].postTimeStamp['toDate'] = convertDateToStartOfTimeStamp(updatedTimeframe[key].rawDate['toDate'].add(1, 'day'));
        updatedTimeframe[key].dayCount = dayjs(updatedTimeframe[key].displayDate.endDate).diff(updatedTimeframe[key].displayDate.fromDate, 'day');
        updatedTimeframe[key].timeframe = +updatedTimeframe[key].dayCount * 86400;
        updatedTimeframe[key].datePicked = false;
      }
    })

    // Necessary translating even selected timeframe is not custom range
    updatedTimeframe['customRange'].label = t(updatedTimeframe['customRange'].label, {
      days: customTimeFrameDay || '365'
    });

    // set item hidden.
    customHideItem.forEach(itemName => updatedTimeframe[itemName].isHide = true);

    return {
      selectedTimeframeKey,
      updatedTimeframe
    }
  }

  const onChangeTimeFrameChecked = (timeframeKey) => {
    const tmpTimeframe = cloneDeep(newTimeframe);
    const { selectedTimeframeKey, updatedTimeframe } = updateSelectedTimeframe(tmpTimeframe, timeframeKey);
    setSelectedTimeframe(updatedTimeframe[selectedTimeframeKey]);
    setNewTimeframe(updatedTimeframe);

    timeframeKey !== 'customRange' && onChange(updatedTimeframe[selectedTimeframeKey]);
  }

  const onOpenDatePicker = (e, type) => {
    e.preventDefault();

    const updatedTimeframe = cloneDeep(newTimeframe);
    Object.keys(updatedTimeframe.customRange.isOpen).forEach(key => updatedTimeframe.customRange.isOpen[key] = false);
    updatedTimeframe.customRange.isOpen[type] = !updatedTimeframe.customRange.isOpen[type];

    setNewTimeframe(updatedTimeframe);
  };

  const onDateChange = (type, date) => {
    const updatedTimeframe = cloneDeep(newTimeframe);

    updatedTimeframe.customRange.isOpen[type] = false;
    updatedTimeframe.customRange.rawDate[type] = date;
    updatedTimeframe.customRange.displayDate[type] = convertDateFormat(date);
    updatedTimeframe.customRange.dayCount = dayjs(updatedTimeframe.customRange.displayDate.endDate).diff(updatedTimeframe.customRange.displayDate.fromDate, 'day');
    updatedTimeframe.customRange.timeframe = +updatedTimeframe.customRange.dayCount * 86400;
    updatedTimeframe.customRange.datePicked = true;

    const newDate = type === 'toDate' ? date.add(1, 'day') : date;
    updatedTimeframe.customRange.postTimeStamp[type] = convertDateToStartOfTimeStamp(newDate);

    setNewTimeframe(updatedTimeframe);
    onChange(updatedTimeframe['customRange'])
  };

  useEffect(() => {
    const { selectedTimeframeKey, updatedTimeframe } = updateSelectedTimeframe(customDefaultTimeframe);
    setSelectedTimeframe(updatedTimeframe[selectedTimeframeKey]);
    onInit && onInit(updatedTimeframe[selectedTimeframeKey]);
    setNewTimeframe(updatedTimeframe);
  }, [forceRerenderObject]);

  return (
    <div className='dropdown-timeframe-container'>
      <button
        className='btn btn-dropdown dropdown-toggle'
        type='button'
        id='license-log-timeframe'
        data-bs-toggle='dropdown'
        disabled={!!disabled}
        aria-expanded='false'
      >
        {selectedTimeframe ? t(selectedTimeframe.label) : ''}
      </button>
      <ul className={`dropdown-menu ${alignEndClassName}`} aria-labelledby='license-log-timeframe' onClick={e => e.stopPropagation()}>
        <>
          {
            Object.keys(newTimeframe).map((key, index) => {
              const tmpTimeframe = newTimeframe[key];
              return (
                <li key={'time-frame-li-' + index} className={tmpTimeframe.isHide ? 'd-none' : ''}>
                  <RadioButton
                    key={'time-frame-radio-' + index}
                    id={tmpTimeframe.id}
                    name='dropdown-with-timeframe'
                    label={tmpTimeframe.label}
                    checked={tmpTimeframe.checked}
                    disabled={disabled}
                    onChange={() => onChangeTimeFrameChecked(tmpTimeframe.id)}
                  />
                </li>
              )
            })
          }
          <li>
            <div className={`${selectedTimeframe && selectedTimeframe.id !== 'customRange' ? 'd-none' : ''} date-picker-group-container`}>
              <div className='date-picker-container'>
                <ButtonWithIcon
                  label={newTimeframe.customRange.displayDate?.fromDate}
                  className='btn-grey-blue'
                  iconClassName='icon-schedule'
                  onClick={e => onOpenDatePicker(e, 'fromDate')}
                ></ButtonWithIcon>
                {newTimeframe.customRange.isOpen.fromDate &&
                  <DatePicker
                    minDate={convertDayjsToDate(dayjs().subtract(customTimeFrameDay, 'day'))}
                    maxDate={convertDayjsToDate(dayjs().toDate())}
                    selected={convertDayjsToDate(newTimeframe.customRange.rawDate.fromDate)}
                    dateFormat={timeFormat.yDate}
                    onChange={date => onDateChange('fromDate', convertDateToDayjs(date))}
                    inline
                  />
                }
              </div>

              <span className='mx-2'> to </span>

              <div className='date-picker-container'>
                <ButtonWithIcon
                  label={newTimeframe.customRange.displayDate?.toDate}
                  className='btn-grey-blue'
                  iconClassName='icon-schedule'
                  onClick={e => onOpenDatePicker(e, 'toDate')}
                ></ButtonWithIcon>
                {newTimeframe.customRange.isOpen.toDate &&
                  <DatePicker
                    minDate={convertDayjsToDate(dayjs().subtract(customTimeFrameDay, 'day'))}
                    maxDate={convertDayjsToDate(dayjs().toDate())}
                    selected={convertDayjsToDate(newTimeframe.customRange.rawDate.toDate)}
                    dateFormat={timeFormat.yDate}
                    onChange={date => onDateChange('toDate', convertDateToDayjs(date))}
                    inline
                  />
                }
              </div>
            </div>
          </li>
        </>
      </ul>
    </div>
  );
};

DropdownWithTimeframe.defaultProps = {
  customDefaultTimeframe: cloneDeep(defaultTimeframe),
  customTimeFrameDay: 365,
  customHideItem: [],
  forceRerenderObject: null
}

export const getTimeFrameSetting = () => cloneDeep(defaultTimeframe);
export const getTimeFrameKey = () => cloneDeep(Object.keys(defaultTimeframe).reduce((a, b) => (a[b] = b, a), {}));
export default memo(DropdownWithTimeframe);
